// useCode: manages the shared-secret ?code= query parameter.
// Persists to sessionStorage for the browser session.

import { useState, useEffect, useCallback } from 'react';

const CODE_KEY = 'esim_radar_code';

export function useCode(): {
  code: string;
  setCode: (code: string) => void;
  isValid: boolean | null; // null = unchecked, true = valid, false = invalid
  validate: (code: string) => Promise<boolean>;
  clearCode: () => void;
} {
  const [code, setCodeState] = useState<string>(() => {
    // Prefer URL ?code= param, fall back to sessionStorage
    const url = new URL(window.location.href);
    const urlCode = url.searchParams.get('code');
    if (urlCode) {
      sessionStorage.setItem(CODE_KEY, urlCode);
      return urlCode;
    }
    return sessionStorage.getItem(CODE_KEY) ?? '';
  });

  const [isValid, setIsValid] = useState<boolean | null>(null);

  const setCode = useCallback((newCode: string) => {
    sessionStorage.setItem(CODE_KEY, newCode);
    setCodeState(newCode);
    setIsValid(null); // Reset validity on code change
  }, []);

  const clearCode = useCallback(() => {
    sessionStorage.removeItem(CODE_KEY);
    setCodeState('');
    setIsValid(false);
  }, []);

  const validate = useCallback(async (codeToTest: string): Promise<boolean> => {
    try {
      const resp = await fetch(`/api/health?code=${encodeURIComponent(codeToTest)}`);
      const valid = resp.status === 200;
      setIsValid(valid);
      if (valid) {
        sessionStorage.setItem(CODE_KEY, codeToTest);
        setCodeState(codeToTest);
      }
      return valid;
    } catch {
      setIsValid(false);
      return false;
    }
  }, []);

  // Auto-validate on mount if we have a code
  useEffect(() => {
    if (code && isValid === null) {
      validate(code).catch(() => {});
    }
  }, [code, isValid, validate]);

  return { code, setCode, isValid, validate, clearCode };
}

/**
 * Append ?code= to a URL if a code is provided.
 */
export function withCode(url: string, code: string): string {
  if (!code) return url;
  const u = new URL(url, window.location.href);
  u.searchParams.set('code', code);
  return u.toString();
}
